import { getCurrentStationId, secTsToRegionalDate } from '@spx-portal/shared-utils';
import dayjs from 'dayjs';

import { CACHE_KEY, CacheType } from '../const';

export function getTimeInterval(time: number | undefined, intervals: string[], beginTime?: number) {
  if (!time) return null;
  const currentTime = dayjs(secTsToRegionalDate(time));
  for (let i = 0; i < intervals.length; i++) {
    const interval = intervals[i];
    const [start, end] = interval.split('-');
    const { hour: startHour, suffix: startSuffix } = parseTime(start.trim());
    const { hour: endHour, suffix: endSuffix } = parseTime(end.trim());

    let startTime = dayjs(beginTime).hour(startHour).startOf('hour');
    let endTime = dayjs(beginTime).hour(startHour).endOf('hour');

    if (startSuffix.endsWith('p.m.') && startTime.hour() < 12) {
      startTime = startTime.add(12, 'hour');
      endTime = endTime.add(12, 'hour');
    }

    if (currentTime.isBefore(startTime) || currentTime.isAfter(endTime)) {
      continue;
    }
    return interval;
  }
  return null;
}

function parseTime(time: string): { hour: number; suffix: string } {
  const match = time.match(/(\d+)\s*(a\.m\.|p\.m\.)/i);
  if (!match) throw new Error(`Invalid time format: ${time}`);

  let hour = parseInt(match[1], 10);
  const suffix = match[2].toLowerCase();

  // Convert to 24-hour format
  if (suffix === 'p.m.' && hour !== 12) hour += 12;
  if (suffix === 'a.m.' && hour === 12) hour = 0;

  return { hour, suffix };
}

export function generateIntervals(numIntervals = 10, beginTime?: number) {
  const currentTime = dayjs(beginTime).startOf('hour');
  const intervals = [];
  let count = 0;

  while (count < numIntervals) {
    const startTime = currentTime.hour() - count;
    const startSuffix = startTime >= 12 ? 'p.m.' : 'a.m.';
    const endTime = currentTime.hour() - count + 1;
    const endSuffix = endTime >= 12 ? 'p.m.' : 'a.m.';
    const startHour = startTime <= 12 ? startTime : startTime - 12;
    const endHour = endTime <= 12 ? endTime : endTime - 12;
    intervals.unshift(`${startHour} ${startSuffix} - ${endHour} ${endSuffix}`);
    count++;
  }

  return intervals;
}

export const transformNum = (num?: number) => {
  if (!num) return '0';
  if (num < 1000) return num.toFixed(0);
  if (num < 1000000) {
    const k = num / 1000;
    return `${k.toFixed(k % 1 === 0 ? 0 : 1)}K`;
  }
  const m = num / 1000000;
  return `${m.toFixed(m % 1 === 0 ? 0 : 1)}M`;
};

export function extractHourAndSuffix(timeRange: string): { startHour: number; startSuffix: string; endHour: number; endSuffix: string } {
  const [start, end] = timeRange.split('-');

  const startTime = parseTime(start.trim());
  const endTime = parseTime(end.trim());

  return {
    startHour: startTime.hour,
    startSuffix: startTime.suffix,
    endHour: endTime.hour,
    endSuffix: endTime.suffix
  };
}

export const getStartHourBySecond = (second?: number) => {
  if (!second) return 0;
  const currentTime = dayjs(secTsToRegionalDate(second));
  return currentTime.hour();
};

export const getWorkstationDashboardCache = (type: CacheType) => {
  try {
    const stationId = getCurrentStationId();
    const cache = localStorage.getItem(CACHE_KEY);
    if (!cache) return null;
    const data = JSON.parse(cache);
    return data[stationId]?.[type] || null;
  } catch (error) {
    return null;
  }
};

export const setWorkstationDashboardCache = (type: CacheType, data: any) => {
  try {
    const stationId = getCurrentStationId();
    let cache = localStorage.getItem(CACHE_KEY);
    if (!cache) {
      localStorage.setItem(CACHE_KEY, JSON.stringify({}));
    }
    const cacheData = cache ? JSON.parse(cache) : {};
    if (!cacheData[stationId]) {
      cacheData[stationId] = {};
    }
    cacheData[stationId][type] = data;
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
  } catch (error) {
    console.error('setCache error', error);
  }
};

export const transformStrToArr = (str?: string) => {
  if (!str) return undefined;
  const arr = str
    .split(',')
    .map(item => Number(item.trim()));
  return arr.length ? arr : undefined;
};
